import { decode as msgPackDecode } from '@msgpack/msgpack/dist/decode';
import { isArray, isEmpty, isString } from 'lodash-es';
import moment from 'moment/moment';
import { Observable } from 'rxjs';

import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { StockExchangeCodeType } from '../../models/fundamental/types';
import GatewayWebsocketState from '../../store-util/GatewayWebsocketState';
import { sleep } from '../../util/CommonHelpers';
import { lz4Unpickle } from '../../util/GatewayHelpers';
import { uuidv4 } from '../../util/tools';
import { NullableTimeout } from '../../util/types';
import { CustomEventService } from '../custom-event/CustomEvent.service';
import { WebSocketEventData, WebSocketEventTypes } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { BaseSubscriptionRequestType } from '../subscription/types';

import { WEB_SOCKET_CONNECTION_TRY_COUNT, WEB_SOCKET_MAX_SUCCESS_STATUS } from './constants';
import { GatewayRequestType, GatewayResponseType } from './types';

const { WebSocket: WebSocketImpl, Response } = configLib;

const LOG_PREFIX = '[BASE-WS]';
const logger = LoggerService.getLogger();
const MANUAL_DISCONNECT_REASON = 'manual_disconnect';

const STOCK_EXCHANGE_CODE_REQUEST_TYPES: GatewayRequestType[] = [
  GatewayRequestType.MarketDataSummary,
  GatewayRequestType.InstrumentSnapshot,
  GatewayRequestType.MarketDataSubscribeTrade,
  GatewayRequestType.MarketDataSubscribeNbbo,
  GatewayRequestType.MarketDataUnsubscribeNbbo,
  GatewayRequestType.MarketDataSubscribeSymbolStatus,
  GatewayRequestType.MarketDataUnsubscribeSymbolStatus,
  GatewayRequestType.AugFXSubscription,
  GatewayRequestType.AugFXUnsubscription,
];

export abstract class BaseWebSocketService<T extends BaseSubscriptionRequestType> {
  private eventService: CustomEventService<any> = new CustomEventService<WebSocketEventData<any>>();

  private webSocket?: WebSocket | null;

  private loginTimeout: PredefinedMilisecondsTypeEnum;

  private loginTimeoutId: NullableTimeout;

  private pingIntervalId: NullableTimeout;

  private isLoggedin = false;

  private connectionTryCount = 0;

  private pendingCloseReason?: string;

  private connectionAttemptId = 0;

  private requestQueue: T[] = [];

  private requestedObjects: T[] = [];// We store requests here with traceIdentifier data to find the relation between the response and the request

  constructor(loginTimeout: PredefinedMilisecondsTypeEnum) {
    this.loginTimeout = loginTimeout;
  }

  public async connect(tryCount = WEB_SOCKET_CONNECTION_TRY_COUNT) {
    if (this.connectionTryCount >= tryCount) {
      logger.warn(`${LOG_PREFIX} max reconnection count exceeded! Please check your connection:`, this.getWebSocketState());
      return;
    }

    if (this.connectionTryCount > 0) {
      // if it's not the first time connection try, wait and try
      await sleep(PredefinedMilisecondsTypeEnum.oneSecond);
    }

    this.connectionTryCount++; // eslint-disable-line no-plusplus
    if (this.isConnected() && this.isAuthenticated()) {
      logger.warn(
        `${LOG_PREFIX} Reconnect will be canceled! it's already been connected and authenticated. state:`,
        this.getWebSocketState(),
      );
      return;
    }

    if (this.isBusy()) {
      logger.warn(`${LOG_PREFIX} Connection is CLOSING/OPENING! Please try again to connect later.state:`, this.getWebSocketState());
      return;
    }

    if (this.isConnected()) {
      logger.warn(`${LOG_PREFIX} WS connection was already established! state:`, this.getWebSocketState());
      return;
    }

    if (this.isConnecting()) {
      logger.warn(`${LOG_PREFIX} Connection attempt already in progress, skipping duplicate request`);
      return;
    }

    this.connectionAttemptId += 1;
    const attemptId = this.connectionAttemptId;
    logger.info(`${LOG_PREFIX} creating new WS connection. state:`, this.getWebSocketState());
    const baseUrl = this.getBaseUrl();
    logger.info(`${LOG_PREFIX} [${attemptId}] connecting`, { url: baseUrl });
    const webSocket = new WebSocketImpl(baseUrl);
    if ('binaryType' in webSocket) {
      // Ensure binary frames arrive as ArrayBuffer for consistent decoding
      (webSocket as any).binaryType = 'arraybuffer';
    }
    this.webSocket = webSocket;
    this.checkLoginStatus();
    this.checkServerByPing();

    // TODO: Cover this with integration test ART-4281
    this.webSocket!.onopen = (): void => {
      if (this.webSocket !== webSocket) return;
      logger.info(`${LOG_PREFIX} [${attemptId}] WebSocket opened`);
      this.resetTryCountToDefault();
    };

    // TODO: Cover this with integration test ART-4281
    this.webSocket!.onmessage = (response: any) => {
      if (this.webSocket !== webSocket) return;
      try {
        this.handleGWWebsocketMessages(response);
      } catch (error) {
        logger.error(`${LOG_PREFIX} Can not handle WebSocket response! error:`, error);
        this.eventService.publishEvent({
          data: response,
          eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
        });
      }
    };

    // TODO: Cover this with integration test ART-4281
    this.webSocket!.onclose = async (event: any): Promise<void> => {
      if (this.webSocket !== webSocket) return;
      const reason = this.pendingCloseReason ?? 'remote_close';
      logger.warn(`${LOG_PREFIX} [${attemptId}] WebSocket connection closed!`, {
        code: event?.code,
        eventReason: event?.reason,
        reason,
        wasClean: event?.wasClean,
        state: this.getWebSocketState(),
      });
      this.pendingCloseReason = undefined;
      this.webSocket = null; // Give an opportunity to Garbage Collector to remove this instance
      this.isLoggedin = false;
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false });
      this.clearConnectionTimers();
      this.eventService.publishEvent({
        data: {
          code: event?.code,
          reason: event?.reason || reason,
          wasClean: event?.wasClean,
        },
        eventType: WebSocketEventTypes.CLOSED,
      });
    };

    // TODO: Cover this with integration test ART-4281
    this.webSocket!.onerror = async (event: any) => {
      if (this.webSocket !== webSocket) return;
      logger.error(`${LOG_PREFIX} [${attemptId}] An error occurred on WebSocket connection create!, event:`, event);
      this.isLoggedin = false;
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false });
      this.eventService.publishEvent({
        data: event,
        eventType: WebSocketEventTypes.ERROR,
      });
    };
  }

  public resetTryCountToDefault(): void {
    this.connectionTryCount = 0;
  }

  public clearRequestQueue(): void {
    this.requestQueue = [];
  }

  public addEventListener(): Observable<WebSocketEventData<any>> {
    return this.eventService.getEventAsObservable();
  }

  async handleGWWebsocketMessages(response: any): Promise<void> {
    let { data: dataRaw } = response;
    const isStringResponse = isString(dataRaw);

    if (!isStringResponse && configLib.isWeb) {
      dataRaw = await new Response(dataRaw).arrayBuffer();// Just in case, this logic was moved from the old implementation
    }

    const responseData = this.getResponseData(isStringResponse, dataRaw);

    const { messageType, resultCode, httpCode } = responseData;
    const isSuccess = this.isSuccessResponse(resultCode, httpCode);

    if (isArray(responseData)) {
      this.eventService.publishEvent({
        data: {
          arrayData: responseData,
          messageType: responseData[0],
          messageSubType: responseData[1][0],
        },
        eventType: WebSocketEventTypes.PARSED_RESPONSE,
        key: uuidv4(),
      });

      return;
    }

    if (isStringResponse && messageType === GatewayResponseType.Login) {
      if (isSuccess) {
        this.isLoggedin = true;
        GatewayWebsocketState.setAll({ isLoggedin: true, isConnected: true });
        this.sendAllQueuedRequest();
      } else {
        this.disconnect();
      }
    }

    if (this.handlePongResponse(messageType, responseData.traceIdentifier)) {
      return;
    }

    this.eventService.publishEvent({
      data: responseData,
      eventType: WebSocketEventTypes.PARSED_RESPONSE,
      key: responseData.traceIdentifier,
    });
  }

  public isConnected = (): boolean => this.webSocket?.readyState === WebSocketImpl.OPEN;

  public isConnecting = (): boolean => this.webSocket?.readyState === WebSocketImpl.CONNECTING;

  public isClosed = (): boolean => this.webSocket?.readyState === WebSocketImpl.CLOSED;

  public isBusy = (): boolean => !!this.webSocket && !this.isConnected() && !this.isClosed();

  public isAuthenticated = (): boolean => this.isConnected() && this.isLoggedin;

  public disconnect(reason = MANUAL_DISCONNECT_REASON): void {
    logger.warn(`${LOG_PREFIX} Socket connection disconnecting... currentStatus:`, this.getWebSocketState());
    const currentSocket = this.webSocket;
    const shouldCloseSocket = !!currentSocket
      && currentSocket.readyState !== WebSocketImpl.CLOSED
      && currentSocket.readyState !== WebSocketImpl.CLOSING;

    if (this.isConnected() || shouldCloseSocket) {
      this.pendingCloseReason = reason;
      this.isLoggedin = false;
      GatewayWebsocketState.setAll({ isLoggedin: false, isConnected: false });
      currentSocket?.close();
    }
    this.clearConnectionTimers();
  }

  public sendRequest(requestBody: T): void {
    if (!this.isConnected()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no WS connection! Data:`, requestBody);
      this.requestQueue.push(requestBody);
      this.connect();
      return;
    }

    if (requestBody.messageType !== GatewayRequestType.Login && !this.isAuthenticated()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no authentication! Data:`, requestBody);
      this.requestQueue.push(requestBody);
      this.connect();

      return;
    }

    if (requestBody.messageType === GatewayRequestType.None) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no messageType! Data:`, requestBody);
      return;
    }

    const outgoing = STOCK_EXCHANGE_CODE_REQUEST_TYPES.includes(requestBody.messageType as GatewayRequestType)
      ? { ...requestBody, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }
      : requestBody;
    this.sendAsString(outgoing as T);
  }

  public getRequestedObjectList(): T[] {
    return this.requestedObjects;
  }

  private removeRequestedObjectByTraceIdentifier(traceIdentifier: string): void {
    this.requestedObjects = this.requestedObjects.filter(item => item.traceIdentifier !== traceIdentifier);
  }

  private handlePongResponse(messageType: GatewayResponseType, traceIdentifier?: string): boolean {
    if (messageType !== GatewayResponseType.Pong) {
      return false;
    }

    logger.debug(`${LOG_PREFIX} Pong received`);
    if (traceIdentifier) {
      this.removeRequestedObjectByTraceIdentifier(traceIdentifier);
    }

    return true;
  }

  private sendAsString(requestBody: T): void {
    this.requestedObjects.push({ ...requestBody, requestTime: moment.utc() });
    const requiredParams: T = { ...requestBody };
    delete requiredParams.optionalParams; // Optional params are for the FE logic. No need to send them to BE
    this.webSocket?.send(JSON.stringify(requiredParams));
  }

    protected abstract getBaseUrl(): string;

    protected abstract cacheResponse(): string;

    private isSuccessResponse(resultCode: any, httpCode: any) {
      const statusCode = resultCode || httpCode;
      return statusCode <= WEB_SOCKET_MAX_SUCCESS_STATUS;
    }

    private getWebSocketState(): string {
      switch (this.webSocket?.readyState) {
        case WebSocketImpl.OPEN:
          return 'OPEN';
        case WebSocketImpl.CONNECTING:
          return 'CONNECTING';
        case WebSocketImpl.CLOSED:
          return 'CLOSED';
        case WebSocketImpl.CLOSING:
          return 'CLOSING';
        default:
          return 'N/A';
      }
    }

    private getResponseData(isStringResponse: boolean, dataRaw: any = {}) {
      let responseData: any = {};
      if (isStringResponse) {
        try {
          return JSON.parse(dataRaw) || {};
        } catch (error) {
          logger.error(`${LOG_PREFIX} Error parsing JSON message! error:${error}`);
          this.eventService.publishEvent({
            data: dataRaw,
            eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
          });
        }

        return responseData;
      }

      // if it is not a String response
      try {
        const uint8Array = new Uint8Array(dataRaw);
        const uncompressed = lz4Unpickle(uint8Array);
        return msgPackDecode(uncompressed) || {};
      } catch (error) {
        logger.error(`${LOG_PREFIX} decode response failed! error:`, error);
        responseData = dataRaw;
        this.eventService.publishEvent({
          data: responseData,
          eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
        });
      }

      return responseData;
    }

    private pingRequest(): void {
      this.sendRequest({
        messageType: GatewayRequestType.Ping,
        traceIdentifier: uuidv4(),
      } as T);
    }

    private sendAllQueuedRequest() {
      if (isEmpty(this.requestQueue)) {
      // Do nothing because there is no queued request to send!
        return;
      }

      this.requestQueue.forEach((item, index) => {
        this.sendRequest(item);
        delete this.requestQueue[index];
      });
    }

    private checkServerByPing() {
      this.pingIntervalId = setInterval(() => {
        if (this.isLoggedin) {
          this.pingRequest();
        }
      }, PredefinedMilisecondsTypeEnum.oneMinute);
    }

    private checkLoginStatus() {
    // If there is no point within a given period, disconnect from the WS server!
      this.loginTimeoutId = setTimeout(() => {
        if (this.isConnected() && !this.isLoggedin) {
        // The connection opened but there is no login event!
          logger.error(`${LOG_PREFIX} Login message was not received before timeout`, {
            timeout: this.loginTimeout,
            state: this.getWebSocketState(),
          });
          this.eventService.publishEvent({
            eventType: WebSocketEventTypes.LOGIN_TIMEOUT,
          });
          this.disconnect('login_timeout');
        }
      }, this.loginTimeout);
    }

    private clearConnectionTimers(): void {
      clearTimeout(this.loginTimeoutId as NodeJS.Timeout);
      clearInterval(this.pingIntervalId as NodeJS.Timeout);
      this.loginTimeoutId = undefined;
      this.pingIntervalId = undefined;
    }
}
